import { addDays, isWithinInterval, parseISO, startOfDay } from 'date-fns';
import { JobItem } from '../../../../app/Slices/SevenDayApiTypes';
import { dayToNumber } from '../../../../shared/utils';
import { AvailableDays, PricingTemplate } from '../../../types/contractor';

type CutoffOptions = {
    contractorCutoff?: number | null;
    templates?: PricingTemplate[];
    templateId?: number | null;
    items?: JobItem[];
};

export type JobBookingAvailability = {
    dateOptions: string[];
    contractorCutoff?: number | null;
    businessDayNumbers?: number[];
    unavailablePeriods?: Array<{ from: string; to: string }>;
    unavailableDays?: number[];
};

type PricingItemCutoff = {
    itemCutoff?: number | null;
    templateCutoff?: number | null;
    contractorCutoff?: number | null;
};

function resolveCutoff({
    itemCutoff,
    templateCutoff,
    contractorCutoff,
}: PricingItemCutoff): number | null {
    if (itemCutoff != null) return itemCutoff;
    if (templateCutoff != null) return templateCutoff;
    if (contractorCutoff != null) return contractorCutoff;
    return null;
}

function toLocalDay(date: Date | string): Date {
    return startOfDay(typeof date === 'string' ? parseISO(date) : date);
}

function addBusinessDays(
    referenceDate: Date,
    businessDayCount: number,
    businessDayNumbers: number[],
): Date {
    const businessDays = new Set(businessDayNumbers);
    let current = startOfDay(referenceDate);
    let counted = 0;

    while (counted < businessDayCount) {
        current = addDays(current, 1);
        if (businessDays.has(current.getDay())) {
            counted++;
        }
    }

    return current;
}

export function getContractorBusinessDayNumbers(
    availableDays?: Array<Pick<AvailableDays, 'day'>>,
): number[] | undefined {
    if (!availableDays?.length) return undefined;

    return availableDays.map((availability) => dayToNumber[availability.day]);
}

export function getEarliestJobRequestDate(
    cutoffDays: number,
    businessDayNumbers?: number[],
): Date {
    const reference = startOfDay(new Date());

    if (businessDayNumbers?.length) {
        return addBusinessDays(reference, cutoffDays, businessDayNumbers);
    }

    return startOfDay(addDays(reference, cutoffDays));
}

export function getEffectiveJobRequestCutoffDays({
    contractorCutoff,
    templates = [],
    templateId,
    items = [],
}: CutoffOptions): number | null {
    const selectedItems = items.filter((item) => item.quantity > 0);

    if (selectedItems.length > 0) {
        const cutoffs = selectedItems
            .map((item) => {
                const template =
                    templates.find((t) => t.id === item.template?.id) ??
                    templates.find((t) => t.id === templateId);
                const pricingItem = template?.items.find(
                    (pi) => pi.id === item.pricingItem?.id,
                );

                return resolveCutoff({
                    itemCutoff: pricingItem?.cutoff,
                    templateCutoff: template?.cutoff,
                    contractorCutoff,
                });
            })
            .filter((cutoff): cutoff is number => cutoff != null);

        return cutoffs.length > 0 ? Math.max(...cutoffs) : null;
    }

    return contractorCutoff ?? null;
}

export function isBeforeJobRequestCutoff(
    date: Date | string,
    cutoffDays: number | null | undefined,
    businessDayNumbers?: number[],
): boolean {
    if (cutoffDays == null || cutoffDays < 0) return false;

    const jobDate = toLocalDay(date);
    const earliestAllowed = getEarliestJobRequestDate(
        cutoffDays,
        businessDayNumbers,
    );

    return jobDate < earliestAllowed;
}

export function isJobDateUnavailable(
    date: string,
    unavailablePeriods: Array<{ from: string; to: string }> = [],
    unavailableDays: number[] = [],
): boolean {
    const jobDate = toLocalDay(date);

    const isUnavailablePeriod = unavailablePeriods.some((period) =>
        isWithinInterval(jobDate, {
            start: toLocalDay(period.from),
            end: toLocalDay(period.to),
        }),
    );

    return isUnavailablePeriod || unavailableDays.includes(jobDate.getDay());
}

export function getItemBookingCutoffLabel(
    cutoff: PricingItemCutoff,
): string | null {
    const days = resolveCutoff(cutoff);
    if (days == null) return null;
    if (days === 0) return 'Can be booked on the same day';

    const unit = days === 1 ? 'business day' : 'business days';
    return `Must be booked at least ${days} ${unit} in advance`;
}

export function isPricingItemAvailableInBookingRange({
    itemCutoff,
    templateCutoff,
    contractorCutoff,
    dateOptions,
    businessDayNumbers,
    unavailablePeriods = [],
    unavailableDays = [],
}: JobBookingAvailability & PricingItemCutoff): boolean {
    if (!dateOptions.length) return false;

    const cutoff = resolveCutoff({
        itemCutoff,
        templateCutoff,
        contractorCutoff,
    });

    return dateOptions.some(
        (date) =>
            !isJobDateUnavailable(date, unavailablePeriods, unavailableDays) &&
            !isBeforeJobRequestCutoff(date, cutoff, businessDayNumbers),
    );
}

export function isTemplateAvailableInBookingRange({
    template,
    ...availability
}: JobBookingAvailability & { template: PricingTemplate }): boolean {
    if (!availability.dateOptions.length) return false;

    if (!template.items?.length) {
        return isPricingItemAvailableInBookingRange({
            ...availability,
            templateCutoff: template.cutoff,
        });
    }

    return template.items.some((item) =>
        isPricingItemAvailableInBookingRange({
            ...availability,
            itemCutoff: item.cutoff,
            templateCutoff: template.cutoff,
        }),
    );
}
